import { HStack, PageContainer, PageHeader, Spread, VStack } from '@/components/Atoms'
import { Block } from '@/components/Block'
import { Note } from '@/components/Note'
import { ExportCsvButton } from '@/components/ExportCsv/ExportCsvButton'
import PermissionsGuard from '@/components/Guard/PermissionsGuard'
import { PendingChanges } from '@/components/PendingChanges'
import usePermissions from '@/hooks/permissions/usePermissions'
import {
  useUpdateWholesalerMarginsNc,
  useWholesalerMarginsNcList,
  wholesalerMarginsNcRawExportQueryOptions,
} from '@/hooks/queries/crt/useWholesalerMarginsNc'
import { useLastUpdatedWholesalerMarginsNcLog } from '@/hooks/queries/useAuditTrails'
import { formatLastUpdatedAssumptionLog } from '@/lib/utils/crt'
import { queryClient } from '@/main'
import { UpdateWholesalerMarginsNc } from '@repo/backend/crt'
import {
  getFormattedPricingSubChannel,
  MarkingCode,
  SUB_CHANNEL_VALUES,
  SubChannel,
} from '@repo/constants'
import { hasPermissions } from '@repo/permissions'
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components'
import { createFileRoute, Link } from '@tanstack/react-router'
import { AlertTriangleIcon, ExternalLinkIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MarginsNcDataTable } from '../../-shared/margins/MarginsNcDataTable'
import { EditedMarkingValues, MarginsNcRow } from '../../-shared/margins/types'
import { MarginsNavigationButtons } from '../../-shared/margins/MarginsNavigationButtons'
import { UploadMarginsDataDialog } from '../../-shared/margins/UploadMarginsDataDialog'

export const Route = createFileRoute(
  '/_auth/_modules/crt/customer-investments/data-sources/wholesaler-margins/nc/',
)({
  component: RouteComponent,
})

function getRowKey(row: MarginsNcRow) {
  return `${row.subChannel}|${row.productType}`
}

function RouteComponent() {
  const { t } = useTranslation(['crt', 'common'])
  const { permissions } = usePermissions()
  const canEdit = hasPermissions(permissions, 'crt_wholesaler_margins_nc_edit')
  const category = t('common.category.wholesaler', { ns: 'common' })

  const [subChannel, setSubChannel] = useState<SubChannel>(SUB_CHANNEL_VALUES[0])
  const [editedValues, setEditedValues] = useState<Map<string, EditedMarkingValues>>(new Map())

  const { data, isPending, error } = useWholesalerMarginsNcList(subChannel)
  const { data: lastUpdatedTrail } = useLastUpdatedWholesalerMarginsNcLog()
  const lastUpdatedLog = formatLastUpdatedAssumptionLog(lastUpdatedTrail)

  const { mutate: updateMargins, isPending: isSaving } = useUpdateWholesalerMarginsNc()

  const uncoveredMaterialsCount = data?.uncoveredMaterialsCount ?? 0
  const rows = (data?.results ?? []) as MarginsNcRow[]

  const handleSubChannelChange = (next: string) => {
    setSubChannel(next as SubChannel)
    setEditedValues(new Map())
  }

  const handleCommitValue = (row: MarginsNcRow, markingKey: MarkingCode, value: string) => {
    const originalValue = row[markingKey] ?? ''
    const key = getRowKey(row)

    setEditedValues((prev) => {
      const next = new Map(prev)
      const existing = next.get(key) ?? {}
      const isUnchanged =
        value === originalValue ||
        (value !== '' && originalValue !== '' && Number(value) === Number(originalValue))

      if (isUnchanged) {
        const rest: EditedMarkingValues = { ...existing }
        delete rest[markingKey]
        if (Object.keys(rest).length === 0) {
          next.delete(key)
        } else {
          next.set(key, rest)
        }
        return next
      }

      next.set(key, { ...existing, [markingKey]: value })
      return next
    })
  }

  const handleSaveChanges = () => {
    if (editedValues.size === 0) return

    const updates: UpdateWholesalerMarginsNc['updates'] = []
    for (const [key, values] of editedValues) {
      const [rowSubChannel, productType] = key.split('|')
      if (!rowSubChannel || !productType) continue
      const normalized: Partial<Record<MarkingCode, string | null>> = {}
      for (const [k, v] of Object.entries(values) as [MarkingCode, string][]) {
        normalized[k] = v === '' ? null : v
      }
      updates.push({
        productType,
        subChannel: rowSubChannel as SubChannel,
        values: normalized,
      })
    }

    updateMargins(
      { updates },
      {
        onSuccess: () => setEditedValues(new Map()),
      },
    )
  }

  const handleBulkEdit = (
    selectedRows: MarginsNcRow[],
    values: Partial<Record<MarkingCode, string>>,
  ) => {
    if (selectedRows.length === 0 || Object.keys(values).length === 0) return

    const updates: UpdateWholesalerMarginsNc['updates'] = selectedRows.map((row) => ({
      productType: row.productType,
      subChannel: row.subChannel as SubChannel,
      values,
    }))

    updateMargins(
      { updates },
      {
        onSuccess: () => {
          setEditedValues((prev) => {
            if (prev.size === 0) return prev
            const next = new Map(prev)
            for (const row of selectedRows) {
              next.delete(getRowKey(row))
            }
            return next.size === prev.size ? prev : next
          })
        },
      },
    )
  }

  const areChangesPending = editedValues.size > 0

  return (
    <PageContainer maxWidth="wide" className="pb-28">
      <Block shouldBlock={areChangesPending} />

      <Spread>
        <PageHeader>{t('margins.nc.pageHeader', { category })}</PageHeader>
        <HStack gap="sm">
          <MarginsNavigationButtons category="wholesaler" kind="nc" />
          <PermissionsGuard required={['crt_wholesaler_margins_nc_edit']}>
            <UploadMarginsDataDialog
              uploadType="import-wholesaler-margins-nc"
              i18nKeyPrefix="importWholesalerMarginsNcDialog"
            />
          </PermissionsGuard>
          <PermissionsGuard required={['crt_wholesaler_margins_nc_view']}>
            <ExportCsvButton
              getCsvExport={() =>
                queryClient.ensureQueryData(wholesalerMarginsNcRawExportQueryOptions())
              }
              rowCount={rows.length}
              canExceedRowCountLimit
            />
          </PermissionsGuard>
        </HStack>
      </Spread>

      {uncoveredMaterialsCount > 0 && (
        <Note
          tone="warning"
          icon={AlertTriangleIcon}
          note={t('margins.nc.uncoveredMaterials.banner', { count: uncoveredMaterialsCount })}
          action={
            <PermissionsGuard required={['crt_product_types_edit']}>
              <Button type="button" variant="secondary" size="sm" asChild>
                <Link to="/crt/materials/data-sources/product-types">
                  <ExternalLinkIcon className="size-4" />
                  {t('margins.nc.uncoveredMaterials.editProductTypes')}
                </Link>
              </Button>
            </PermissionsGuard>
          }
        />
      )}

      <VStack gap="md">
        <Select value={subChannel} onValueChange={handleSubChannelChange}>
          <SelectTrigger id="wholesaler-nc-sub-channel-select" className="w-72 bg-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SUB_CHANNEL_VALUES.map((value) => (
              <SelectItem key={value} value={value}>
                {getFormattedPricingSubChannel(value)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <MarginsNcDataTable
          rows={rows}
          isPending={isPending}
          error={error}
          editedValues={editedValues}
          canEdit={canEdit}
          category={category}
          domIdPrefix="wholesaler-nc"
          getRowKey={getRowKey}
          onCommitValue={handleCommitValue}
          onBulkEdit={handleBulkEdit}
        />
      </VStack>

      <PermissionsGuard required={['crt_wholesaler_margins_nc_edit']}>
        <PendingChanges
          areChangesPending={areChangesPending}
          lastUpdatedLog={lastUpdatedLog}
          isSavePending={isSaving}
          onSaveChanges={handleSaveChanges}
        />
      </PermissionsGuard>
    </PageContainer>
  )
}
